<section class="validate-arm-pool-page">
  @if (isModalOpen) {
    <div class="validate-arm-pool-modal-backdrop">
      <div class="validate-arm-pool-modal" role="dialog" aria-labelledby="validate-arm-pool-title">
        <div class="validate-arm-pool-modal-header">
          <h2 id="validate-arm-pool-title" class="validate-arm-pool-modal-title">
            Validate ARM Pool
          </h2>
        </div>
        <div class="validate-arm-pool-modal-body">
          <div class="validate-arm-pool-alert" role="status" aria-live="polite">
            <div class="validate-arm-pool-alert-icon-wrap">
              <i class="bi bi-info-circle validate-arm-pool-alert-icon" aria-hidden="true"></i>
            </div>
            <div class="validate-arm-pool-alert-content">
              <p class="validate-arm-pool-alert-text">
                Upload a text or Excel file with CUSIP and Original Face Amount to validate ARM
                pools for CMO collateral use.
              </p>
            </div>
          </div>

          @if (showValidationAlert) {
            <div
              class="validate-arm-pool-alert"
              [class.validate-arm-pool-alert-success]="isSuccessAlert"
              [class.validate-arm-pool-alert-danger]="!isSuccessAlert"
              role="alert"
              aria-live="assertive"
            >
              <div class="validate-arm-pool-alert-icon-wrap">
                <i
                  class="bi validate-arm-pool-alert-icon"
                  [class.bi-check-circle-fill]="isSuccessAlert"
                  [class.bi-exclamation-circle-fill]="!isSuccessAlert"
                  [class.success-icon]="isSuccessAlert"
                  aria-hidden="true"
                ></i>
              </div>
              <div class="validate-arm-pool-alert-content">
                <p class="validate-arm-pool-alert-text">{{ validationAlertMessage }}</p>
              </div>
            </div>
          }

          <div class="validate-arm-pool-input-item">
            <div class="validate-arm-pool-input-item-container">
              <label class="validate-arm-pool-input-label" for="validate-arm-pool-file">
                <span>Pool File</span>
                <span class="validate-arm-pool-required-asterisk">*</span>
              </label>

              <div class="validate-arm-pool-input-field-group">
                <div
                  class="validate-arm-pool-file-picker-row"
                  [class.validate-arm-pool-file-picker-row-invalid]="hasPoolFileValidationError"
                >
                  <input
                    #poolFileInput
                    id="validate-arm-pool-file"
                    class="validate-arm-pool-file-input"
                    type="file"
                    accept=".txt,.csv,.xls,.xlsx"
                    (change)="onPoolFileSelected($event)"
                  />
                  <label class="validate-arm-pool-choose-file" for="validate-arm-pool-file"
                    >Choose File</label
                  >
                  <span class="validate-arm-pool-selected-file">{{ selectedPoolFileName }}</span>
                </div>
              </div>

              <div class="validate-arm-pool-input-feedback">
                <p
                  class="validate-arm-pool-input-feedback-text"
                  [class.validate-arm-pool-input-feedback-text-invalid]="hasFileTypeValidationError"
                >
                  Formats accepted: txt, csv, xls, xlsx
                </p>
              </div>
            </div>
          </div>

          <div class="validate-arm-pool-period-field">
            <label class="validate-arm-pool-period-label" for="validate-arm-pool-period">
              <span>Period</span>
              <span class="validate-arm-pool-required-asterisk">*</span>
            </label>

            <div class="validate-arm-pool-period-control-group">
              <div
                class="validate-arm-pool-period-input-wrap"
                (click)="togglePeriodMonthPicker($event)"
              >
                <input
                  id="validate-arm-pool-period"
                  class="validate-arm-pool-period-input"
                  [class.validate-arm-pool-input-invalid]="hasPeriodValidationError"
                  type="text"
                  placeholder="MM/YYYY"
                  aria-label="Period"
                  [value]="periodDisplayValue"
                  readonly
                />
                <i
                  class="bi bi-calendar3 validate-arm-pool-period-calendar-icon"
                  aria-hidden="true"
                ></i>

                @if (periodMonthPickerOpen) {
                  <div
                    class="validate-arm-pool-month-picker-dropdown"
                    (click)="$event.stopPropagation()"
                  >
                    <div class="validate-arm-pool-month-picker-header">
                      <button
                        type="button"
                        class="validate-arm-pool-month-nav-btn"
                        (click)="changePeriodYear(-1, $event)"
                        aria-label="Previous year"
                      >
                        <i class="bi bi-chevron-left"></i>
                      </button>
                      <span class="validate-arm-pool-month-picker-year">{{
                        periodPickerYear
                      }}</span>
                      <button
                        type="button"
                        class="validate-arm-pool-month-nav-btn"
                        (click)="changePeriodYear(1, $event)"
                        aria-label="Next year"
                      >
                        <i class="bi bi-chevron-right"></i>
                      </button>
                    </div>

                    <div class="validate-arm-pool-month-picker-grid">
                      @for (month of monthNames; track month.index) {
                        <button
                          type="button"
                          class="validate-arm-pool-month-picker-item"
                          [class.active]="isSelectedPeriodMonth(month.index)"
                          (click)="selectPeriodMonth(month.index, $event)"
                        >
                          {{ month.short }}
                        </button>
                      }
                    </div>
                  </div>
                }
              </div>
            </div>
          </div>

          <div class="validate-arm-pool-collateral-group-field">
            <label
              class="validate-arm-pool-collateral-group-label"
              for="validate-arm-pool-collateral-group-number"
            >
              <span>Collateral Group Number</span>
              <span class="validate-arm-pool-required-asterisk">*</span>
            </label>

            <div class="validate-arm-pool-collateral-group-control-group">
              <input
                id="validate-arm-pool-collateral-group-number"
                class="validate-arm-pool-collateral-group-input"
                [class.validate-arm-pool-input-invalid]="hasCollateralGroupValidationError"
                type="text"
                [value]="collateralGroupNumberValue"
                (click)="onCollateralGroupNumberClick($event)"
                (input)="onCollateralGroupNumberInput($event)"
                aria-label="Collateral Group Number"
              />
            </div>
          </div>

          <div class="validate-arm-pool-detailed-report-field">
            <label
              class="validate-arm-pool-detailed-report-label"
              for="validate-arm-pool-detailed-report"
            >
              <input
                id="validate-arm-pool-detailed-report"
                class="validate-arm-pool-detailed-report-checkbox"
                type="checkbox"
                [checked]="detailed"
                (change)="onDetailedChange($event)"
              />
              <span class="validate-arm-pool-detailed-report-text"
                >I want a detailed report with subtypes and prefixes</span
              >
            </label>
          </div>
        </div>

        <div class="validate-arm-pool-modal-footer">
          <div class="validate-arm-pool-modal-footer-actions">
            <button
              type="button"
              class="validate-arm-pool-btn validate-arm-pool-btn-outline"
              (click)="onReset(poolFileInput)"
            >
              Reset
            </button>
            <button
              type="button"
              class="validate-arm-pool-btn validate-arm-pool-btn-primary"
              [disabled]="isSubmitting"
              (click)="onEmailReport()"
            >
              Email Report
            </button>
          </div>
        </div>
      </div>
    </div>
  }
</section>
